/*
 * Môdern Studio™ — Phase 6B: move browser Projects/Homes to the account
 * ------------------------------------------------------------------------
 * Approved design: Phase 6B rev3 (D1–D23) with review corrections.
 *
 * Loaded ONLY on My Projects (sections/studio-my-projects.liquid, inside the
 * cloud_projects_enabled block, after studio-cloud-projects.js). Nothing is
 * loaded or run on any other page.
 *
 * Hard rules this file follows:
 *   - Browser copies are READ, never written, moved or deleted. The only
 *     localStorage key this file writes is the status-only marker
 *     modernStudioCloudMigration:<customerId> ({deferredUntil, resolvedIds,
 *     unscopedDecision}) via MSGProjects — ids and statuses only (D4).
 *   - No customerId, email or other identity value is ever sent. Identity
 *     comes only from Shopify's signed App Proxy request.
 *   - Preview (dryRun) requests are stateless: every Project batch carries
 *     the Homes its Projects point to in `plannedHomes` (D16). The real
 *     import never sends `plannedHomes` (D18).
 *   - Nothing is sent without a customer action. Unknown outcomes are
 *     checked with a dry run; anything that still needs resending waits for
 *     the customer's "Continue".
 *   - The real import's results are the authority; the review screen is a
 *     preview, never a promise.
 */
(function () {
  'use strict';

  var PROXY_BASE = '/apps/modern-studio';
  var IMPORT_PATH = PROXY_BASE + '/projects-import';
  var MAX_PER_ARRAY = 200;
  var MAX_REQUEST_BYTES = 3 * 1024 * 1024;
  var MAX_REQUESTS_PER_MINUTE = 6;
  var PREVIEW_MAX_AGE_MS = 10 * 60 * 1000;
  var DEFER_MS = 7 * 24 * 60 * 60 * 1000;
  var MAX_NAME = 200;
  var MAX_ROOM_LABEL = 80;

  // Tool room keys (kept inside moodBoard.room / spacePlan.collection) ->
  // canonical Project room. Same table Mood Board / Space Planner use.
  var ROOM_TO_PROJECT_ROOM = {
    'bedroom-collection': 'bedroom',
    'living-room-collection': 'livingroom',
    'kitchen-collection': 'kitchen',
    'bathroom-collection': 'bathroom',
    'office-furniture': 'homeoffice',
    'outdoor-furniture': 'outdoor'
  };
  var CANONICAL_ROOMS = ['livingroom', 'bedroom', 'kitchen', 'bathroom', 'diningroom', 'homeoffice', 'outdoor'];
  var ROOM_LABELS = {
    livingroom: 'Living Room', bedroom: 'Bedroom', kitchen: 'Kitchen', bathroom: 'Bathroom',
    diningroom: 'Dining Room', homeoffice: 'Home Office', outdoor: 'Outdoor'
  };

  // Test seam for time (fixed clocks, fast pacing in tests).
  // #16: every request (move and account check alike) gets a time limit, so
  // a request the browser never answers can't leave the page on "Moving…".
  var timing = {
    now: function () { return Date.now(); },
    sleep: function (ms) { return new Promise(function (r) { setTimeout(r, ms); }); },
    requestTimeoutMs: 20000
  };

  // #16: the browser already says there is no connection.
  function isOffline() {
    return typeof navigator !== 'undefined' && navigator.onLine === false;
  }

  // ==========================================================================
  // Pure helpers (no DOM, no network)
  // ==========================================================================

  function isObj(v) { return typeof v === 'object' && v !== null && !Array.isArray(v); }
  function str(v) { return typeof v === 'string' ? v : ''; }
  function trimmed(v) { return typeof v === 'string' ? v.trim() : ''; }

  function resolveRoom(project) {
    var candidates = [project.room, isObj(project.moodBoard) ? project.moodBoard.room : null,
      isObj(project.spacePlan) ? project.spacePlan.collection : null];
    for (var i = 0; i < candidates.length; i++) {
      var c = candidates[i];
      if (typeof c !== 'string') continue;
      if (ROOM_TO_PROJECT_ROOM[c]) return ROOM_TO_PROJECT_ROOM[c];
      if (CANONICAL_ROOMS.indexOf(c) !== -1) return c;
    }
    return null;
  }

  // Inspiration items keep only title, url and image (D20) — exactly what
  // cloud saves already store; nothing else is read by the Mood Board.
  function cleanBoard(board) {
    if (!isObj(board)) return board === undefined ? undefined : null;
    var out = {};
    Object.keys(board).forEach(function (k) {
      if (k !== 'inspiration') out[k] = board[k];
    });
    if (Array.isArray(board.inspiration)) {
      out.inspiration = board.inspiration.map(function (item) {
        if (!isObj(item)) return item;
        var o = { title: item.title, url: item.url };
        if (typeof item.image === 'string' && item.image) o.image = item.image;
        return o;
      });
    }
    return out;
  }

  function nameCheck(value) {
    var t = trimmed(value);
    if (!t) return 'blank';
    if (typeof value === 'string' && value.length > MAX_NAME) return 'too_long';
    return null;
  }

  function suggestedProjectName(project, room) {
    var label = (typeof project.roomLabel === 'string' && project.roomLabel.trim() && project.roomLabel.length <= MAX_ROOM_LABEL)
      ? project.roomLabel.trim() : (ROOM_LABELS[room] || 'Room');
    return label + ' Project';
  }

  /**
   * Build the migration inventory from browser records and the cloud lists.
   * local: { scoped: {projects, homes}, unscoped: {projects, homes} }
   * Returns { homes: [HomeRow], projects: [ProjectRow], unscopedCount, hasUnscoped }
   */
  function buildInventory(local, cloudProjects, cloudHomes, marker, includeUnscoped) {
    var resolved = (marker && marker.resolvedIds) || {};
    var cloudIds = {};
    (cloudProjects || []).forEach(function (p) { if (p && p.clientLegacyId) cloudIds[p.clientLegacyId] = true; });
    (cloudHomes || []).forEach(function (h) { if (h && h.clientLegacyId) cloudIds[h.clientLegacyId] = true; });

    function isResolved(id) { return !!cloudIds[id] || !!resolved[id]; }

    var seenHome = {};
    var homes = [];
    function addHomes(list, source) {
      (Array.isArray(list) ? list : []).forEach(function (h) {
        if (!isObj(h) || typeof h.id !== 'string' || !h.id.trim()) return;
        var id = h.id.trim();
        if (seenHome[id]) return;
        seenHome[id] = true;
        homes.push({ kind: 'home', legacyId: id, source: source, raw: h });
      });
    }
    addHomes(local.scoped.homes, 'scoped');
    var unscopedHomes = (local.unscoped && local.unscoped.homes) || [];
    if (includeUnscoped) addHomes(unscopedHomes, 'unscoped');

    var seenProject = {};
    var projects = [];
    function addProjects(list, source) {
      (Array.isArray(list) ? list : []).forEach(function (p) {
        if (!isObj(p) || typeof p.id !== 'string' || !p.id.trim()) return;
        var id = p.id.trim();
        if (seenProject[id]) return;
        seenProject[id] = true;
        projects.push({ kind: 'project', legacyId: id, source: source, raw: p });
      });
    }
    addProjects(local.scoped.projects, 'scoped');
    var unscopedProjects = (local.unscoped && local.unscoped.projects) || [];
    if (includeUnscoped) addProjects(unscopedProjects, 'unscoped');

    // Membership: first Home (in list order) that lists the Project wins.
    var homeOf = {};
    var alsoIn = {};
    homes.forEach(function (h) {
      (Array.isArray(h.raw.projectIds) ? h.raw.projectIds : []).forEach(function (pid) {
        if (typeof pid !== 'string') return;
        if (!homeOf[pid]) homeOf[pid] = h.legacyId;
        else if (homeOf[pid] !== h.legacyId) (alsoIn[pid] = alsoIn[pid] || []).push(h.legacyId);
      });
    });

    // Fix A: a Project listed only in a Home saved before sign-in (the older,
    // unscoped storage) while those items are NOT part of this move (the
    // customer hasn't confirmed they're theirs, or left them out). That Home
    // is never read into the move; the Project records the relationship so
    // the review can show it and ask. Never a silent homeId: null.
    var cloudHomeIds = {};
    (cloudHomes || []).forEach(function (h) { if (h && h.clientLegacyId) cloudHomeIds[h.clientLegacyId] = true; });
    var outsideHomeOf = {};
    if (!includeUnscoped) {
      (Array.isArray(unscopedHomes) ? unscopedHomes : []).forEach(function (h) {
        if (!isObj(h) || typeof h.id !== 'string' || !h.id.trim()) return;
        var hidTrim = h.id.trim();
        (Array.isArray(h.projectIds) ? h.projectIds : []).forEach(function (pid) {
          if (typeof pid !== 'string' || outsideHomeOf[pid]) return;
          outsideHomeOf[pid] = {
            legacyId: hidTrim,
            name: str(h.name),
            // Already in this customer's account (server-scoped list), so
            // linking by lookup doesn't move anything unconfirmed.
            inAccount: !!cloudHomeIds[hidTrim] || resolved[hidTrim] === 'moved'
          };
        });
      });
    }
    var unscopedDecision = (marker && marker.unscopedDecision) || null;

    homes.forEach(function (h) {
      h.resolved = isResolved(h.legacyId);
      h.resolvedAs = cloudIds[h.legacyId] ? 'in_account' : (resolved[h.legacyId] || null);
      h.name = str(h.raw.name);
      h.editedName = null;
      h.ticked = !h.resolved;
      var n = nameCheck(h.raw.name);
      h.state = h.resolved ? 'resolved' : (n ? 'needs_edit' : 'candidate');
      h.editReason = n;
      h.suggestedName = null; // a Home has no suggestion: the customer names it
    });

    projects.forEach(function (p) {
      p.resolved = isResolved(p.legacyId);
      p.room = resolveRoom(p.raw);
      p.homeLegacyId = homeOf[p.legacyId] || null;
      p.outsideHome = p.homeLegacyId ? null : (outsideHomeOf[p.legacyId] || null);
      if (p.outsideHome) p.outsideHome.decision = unscopedDecision;
      p.alsoInHomes = alsoIn[p.legacyId] || [];
      p.withoutHome = false;
      p.datePreference = null;
      p.editedName = null;
      p.suggestedName = null;
      p.ticked = !p.resolved;
      var n = nameCheck(p.raw.name);
      p.editReason = n;
      if (p.resolved) p.state = 'resolved';
      else if (!p.room) { p.state = 'not_movable'; p.notMovableReason = 'room'; p.ticked = false; }
      // A suggestion is only SHOWN in the edit field; nothing is sent with it
      // until the customer checks the change (fix-ups need consent).
      else if (n) { p.state = 'needs_edit'; if (n === 'blank') p.suggestedName = suggestedProjectName(p.raw, p.room); }
      else p.state = 'candidate';
      p.roomLabelReplaced = typeof p.raw.roomLabel === 'string' && p.raw.roomLabel.length > MAX_ROOM_LABEL;
    });

    return {
      homes: homes,
      projects: projects,
      hasUnscoped: (unscopedHomes.length + unscopedProjects.length) > 0,
      unscopedCount: unscopedProjects.length + unscopedHomes.length
    };
  }

  function homeById(inv, id) {
    for (var i = 0; i < inv.homes.length; i++) if (inv.homes[i].legacyId === id) return inv.homes[i];
    return null;
  }

  // The name sent to the server: the saved name, or the customer's checked edit.
  function currentName(row) {
    return row.editedName !== null && row.editedName !== undefined ? row.editedName : str(row.raw.name);
  }

  // What the edit field shows: the customer's edit, else the suggestion, else the saved name.
  function fieldValue(row) {
    if (row.editedName !== null && row.editedName !== undefined) return row.editedName;
    if (row.suggestedName) return row.suggestedName;
    return str(row.raw.name);
  }

  function homePayload(h) {
    var out = { id: h.legacyId, name: currentName(h) };
    if (h.raw.createdAt !== undefined) out.createdAt = h.raw.createdAt;
    if (h.raw.updatedAt !== undefined) out.updatedAt = h.raw.updatedAt;
    if (h.datePreference) out.datePreference = h.datePreference;
    return out;
  }

  // How a Project's Home is treated right now (review-screen line + payload).
  // Returns { homeId: string|null, line: code, homeRow: HomeRow|null }
  function projectHomePlan(inv, p) {
    if (!p.homeLegacyId) {
      var o = p.outsideHome;
      if (!o) return { homeId: null, line: 'none', homeRow: null };
      if (o.inAccount) return { homeId: o.legacyId, line: 'outside_in_account', homeRow: null, outside: o };
      if (p.withoutHome) return { homeId: null, line: 'without_outside', homeRow: null, outside: o };
      // Needs the customer's explicit choice before it can move.
      return { homeId: null, line: 'ask_outside', homeRow: null, outside: o };
    }
    var h = homeById(inv, p.homeLegacyId);
    if (!h) return { homeId: null, line: 'none', homeRow: null };
    var deleted = h.state === 'deleted_in_account' || (h.resolved && h.resolvedAs === 'deleted_in_account');
    if (h.resolved && !deleted) {
      // Already moved earlier (or in the account): link by lookup.
      return { homeId: h.legacyId, line: 'already_in_account', homeRow: h };
    }
    if (h.state === 'already_in_account') return { homeId: h.legacyId, line: 'already_in_account', homeRow: h };
    if (deleted) {
      return p.withoutHome
        ? { homeId: null, line: 'without_deleted', homeRow: h }
        : { homeId: h.legacyId, line: 'ask_deleted', homeRow: h };
    }
    if (h.state === 'not_movable') return { homeId: null, line: 'without_not_movable', homeRow: h };
    if (p.withoutHome) return { homeId: null, line: 'without_chosen', homeRow: h };
    if (!h.ticked) return { homeId: null, line: 'without_unticked', homeRow: h };
    if (h.state === 'needs_edit' || h.state === 'edited_unchecked') return { homeId: h.legacyId, line: 'waiting_home', homeRow: h };
    return { homeId: h.legacyId, line: 'grouped', homeRow: h };
  }

  // A Project whose Home situation needs the customer's explicit choice
  // (Home deleted in the account, or a Home saved before sign-in that stays
  // behind). Such a Project can never be part of a real import.
  function needsHomeChoice(inv, p) {
    var l = projectHomePlan(inv, p).line;
    return l === 'ask_deleted' || l === 'ask_outside';
  }

  function projectPayload(inv, p) {
    var raw = p.raw;
    var room = p.room;
    var out = { id: p.legacyId, homeId: projectHomePlan(inv, p).homeId, name: currentName(p), room: room };
    var label = typeof raw.roomLabel === 'string' ? raw.roomLabel : null;
    if (label !== null) out.roomLabel = label.length > MAX_ROOM_LABEL ? (ROOM_LABELS[room] || null) : label;
    if (raw.moodBoard !== undefined) out.moodBoard = cleanBoard(raw.moodBoard);
    if (raw.spacePlan !== undefined) out.spacePlan = raw.spacePlan;
    if (raw.createdAt !== undefined) out.createdAt = raw.createdAt;
    if (raw.updatedAt !== undefined) out.updatedAt = raw.updatedAt;
    if (p.datePreference) out.datePreference = p.datePreference;
    return out;
  }

  // Fingerprints (D22): a Project's includes its planned Home id + name.
  function homeFingerprint(h) { return JSON.stringify(homePayload(h)); }
  function projectFingerprint(inv, p) {
    var plan = projectHomePlan(inv, p);
    var planned = null;
    if (plan.homeId && plan.homeRow && !plan.homeRow.resolved && plan.homeRow.state !== 'already_in_account') {
      planned = { id: plan.homeRow.legacyId, name: currentName(plan.homeRow) };
    }
    return JSON.stringify(projectPayload(inv, p)) + '|' + JSON.stringify(planned);
  }

  function chunk(list, size) {
    var out = [];
    for (var i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
    return out;
  }

  function byteLength(obj) {
    var s = JSON.stringify(obj);
    try { return new Blob([s]).size; } catch (e) { return s.length * 2; }
  }

  // Split a batch further if its JSON body would exceed the byte cap.
  function sizeSplit(items, makeBody) {
    var out = [];
    (function split(list) {
      if (!list.length) return;
      if (list.length === 1 || byteLength(makeBody(list)) <= MAX_REQUEST_BYTES) { out.push(list); return; }
      var mid = Math.ceil(list.length / 2);
      split(list.slice(0, mid));
      split(list.slice(mid));
    })(items);
    return out;
  }

  /**
   * Preview requests (P2 Home status batches, then P3 Project batches).
   * rows: which rows to (re)check. Every Project batch carries its own
   * plannedHomes — the ticked, not-yet-moved Homes its Projects point to.
   */
  function previewRequests(inv, homeRows, projectRows, planVersion) {
    var reqs = [];
    chunk(homeRows, MAX_PER_ARRAY).forEach(function (batch) {
      sizeSplit(batch, function (l) { return { dryRun: true, planVersion: planVersion, homes: l.map(homePayload), projects: [] }; })
        .forEach(function (l) {
          reqs.push({ kind: 'homes', rows: l, body: { dryRun: true, planVersion: planVersion, homes: l.map(homePayload), projects: [] } });
        });
    });
    chunk(projectRows, MAX_PER_ARRAY).forEach(function (batch) {
      var make = function (l) {
        var planned = [];
        var seen = {};
        l.forEach(function (p) {
          var plan = projectHomePlan(inv, p);
          var h = plan.homeRow;
          if (plan.homeId && h && !h.resolved && h.state !== 'already_in_account' && !seen[h.legacyId]) {
            seen[h.legacyId] = true;
            planned.push({ id: h.legacyId, name: currentName(h) });
          }
        });
        var body = { dryRun: true, planVersion: planVersion, homes: [], projects: l.map(function (p) { return projectPayload(inv, p); }) };
        if (planned.length) body.plannedHomes = planned;
        return body;
      };
      sizeSplit(batch, make).forEach(function (l) {
        reqs.push({ kind: 'projects', rows: l, body: make(l) });
      });
    });
    return reqs;
  }

  // Real import requests: I1 Home batches, then I2 Project batches. Never plannedHomes.
  function importHomeRequests(homeRows) {
    var reqs = [];
    chunk(homeRows, MAX_PER_ARRAY).forEach(function (batch) {
      sizeSplit(batch, function (l) { return { homes: l.map(homePayload), projects: [] }; }).forEach(function (l) {
        reqs.push({ kind: 'homes', rows: l, body: { homes: l.map(homePayload), projects: [] } });
      });
    });
    return reqs;
  }
  function importProjectRequests(inv, projectRows) {
    var reqs = [];
    chunk(projectRows, MAX_PER_ARRAY).forEach(function (batch) {
      var make = function (l) { return { homes: [], projects: l.map(function (p) { return projectPayload(inv, p); }) }; };
      sizeSplit(batch, make).forEach(function (l) { reqs.push({ kind: 'projects', rows: l, body: make(l) }); });
    });
    return reqs;
  }

  // ==========================================================================
  // Network (App Proxy, same-origin). Classifies every outcome.
  // ==========================================================================

  var sentAt = []; // timestamps of requests, for pacing (<= 6 per minute)

  function pace() {
    var now = timing.now();
    sentAt = sentAt.filter(function (t) { return now - t < 60000; });
    if (sentAt.length < MAX_REQUESTS_PER_MINUTE) { sentAt.push(now); return Promise.resolve(0); }
    var wait = 60000 - (now - sentAt[0]) + 50;
    return timing.sleep(wait).then(function () { return pace(); }).then(function (w) { return wait + w; });
  }

  function send(body) {
    return pace().then(function () {
      // #16: offline before sending -> nothing is sent at all, so the outcome
      // is certain (not sent). The pacing slot reserved for it is released.
      if (isOffline()) {
        sentAt.pop();
        return { kind: 'offline' };
      }
      // Phase 6B #3: never send while account saving isn't confirmed on
      // (e.g. it was switched off during this visit). Nothing is sent, so
      // this is a definite "unavailable", not an unknown outcome.
      var store = window.MSGProjects;
      if (!store || typeof store.getAccountWrites !== 'function' || store.getAccountWrites() !== 'on') {
        sentAt.pop();
        return { kind: 'unavailable' };
      }
      return new Promise(function (resolve) {
        var settled = false;
        var controller = typeof AbortController === 'function' ? new AbortController() : null;
        var timer = setTimeout(function () {
          // #16: no complete answer in time. The request may still have
          // reached the account, so this is an UNKNOWN outcome even though it
          // is aborted here: the caller checks the account before any retry.
          if (controller) { try { controller.abort(); } catch (e) { /* ignore */ } }
          settle({ kind: 'unknown', reason: 'timeout' });
        }, timing.requestTimeoutMs);
        // Only the first outcome counts: a reply that arrives after the time
        // limit can never change the screen or start another step.
        function settle(out) {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve(out);
        }
        var init = {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        };
        if (controller) init.signal = controller.signal;
        fetch(IMPORT_PATH, init).then(classify).then(settle, function () {
          settle({ kind: 'unknown' });
        });
      });
    });
  }

  // Classifies every answer the server gives (unchanged rules).
  function classify(res) {
    return res.json().catch(function () { return null; }).then(function (data) {
        if (res.status === 200) {
          // Fix B: the server accepted the request, but its answer was lost
          // or unreadable (e.g. the connection dropped mid-body). The outcome
          // is UNKNOWN: it is checked with a dry run, never called a failure
          // and never resent blindly.
          return isObj(data) ? { kind: 'ok', data: data } : { kind: 'unknown', reason: 'unreadable' };
        }
        if (res.status === 404) return { kind: 'unavailable' };
        if (res.status === 403) return { kind: 'not_entitled' };
        if (res.status === 401) return { kind: 'signed_out' };
        if (res.status === 429) return { kind: 'rate_limited' };
        if (res.status >= 500) return { kind: 'unknown' };
        return { kind: 'rejected' };
    });
  }

  // ==========================================================================
  // Text
  // ==========================================================================

  function esc(s) {
    var d = document.createElement('div');
    d.textContent = s == null ? '' : String(s);
    return d.innerHTML;
  }
  function q(s) { return '“' + s + '”'; }
  function fmtDate(ms) {
    try { return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); } catch (e) { return ''; }
  }
  function plural(n, one, many) { return n + ' ' + (n === 1 ? one : many); }

  var FIELD_TEXT = {
    name: 'the name is missing or too long',
    room: 'its room could not be determined',
    roomLabel: 'its room label could not be read',
    moodBoard: 'its Mood Board could not be read',
    spacePlan: 'its Space Plan could not be read',
    homeId: 'its Home reference could not be read',
    id: 'it has no readable ID',
    datePreference: 'its date choice could not be read'
  };
  function fieldText(field) {
    if (!field) return 'it could not be read';
    if (FIELD_TEXT[field]) return FIELD_TEXT[field];
    if (field.indexOf('moodBoard') === 0) return 'part of its Mood Board could not be read';
    if (field.indexOf('spacePlan') === 0) return 'part of its Space Plan could not be read';
    return 'it could not be read';
  }

  function summaryOf(p) {
    var bits = [];
    if (p.raw.moodBoard) bits.push('Mood Board');
    if (p.raw.spacePlan) bits.push('Space Plan');
    return bits.length ? bits.join(', ') : 'No board or plan yet';
  }

  // Date notes (D5 + correction 2: import-time fallbacks are "the day it's
  // moved", never promised as an exact timestamp).
  function dateNotes(dates, noun) {
    if (!dates) return [];
    var out = [];
    var c = dates.createdAt;
    var u = dates.updatedAt;
    if (c) {
      if (c.source === 'record' && c.note === 'earlier_than_expected') {
        out.push({ text: 'Created ' + fmtDate(c.value) + ' — earlier than we expected. It will be kept as saved; your device’s clock may have been set differently.', optIn: true });
      } else if (c.source === 'id') {
        out.push({ text: 'Created date couldn’t be read, so we used when this ' + noun + ' was first saved in this browser (' + fmtDate(c.value) + ').' });
      } else if (c.source === 'import') {
        out.push({ text: (c.reason === 'future' ? 'Created date is in the future' : 'Created date couldn’t be read') + ', so it will show the day it’s moved to your account.' });
      } else if (c.source === 'customer_choice') {
        out.push({ text: 'Will show the day it’s moved to your account, as you chose.' });
      }
    }
    if (u && c && c.source !== 'customer_choice') {
      if (u.source === 'created' && u.reason === 'order_fixed') out.push({ text: 'Last-updated date was earlier than its created date, so it will show its created date.' });
      else if (u.source === 'saved_date') out.push({ text: 'Last-updated date taken from its ' + (u.from === 'spacePlan' ? 'Space Plan' : 'Mood Board') + ' (' + fmtDate(u.value) + ').' });
      else if (u.source === 'created' && u.reason) out.push({ text: 'Last-updated date is missing or couldn’t be read, so it will show its created date.' });
    }
    ['boardSavedAt', 'planSavedAt'].forEach(function (k) {
      var s = dates[k];
      if (s && s.source === 'fallback') {
        out.push({ text: 'The ' + (k === 'boardSavedAt' ? 'Mood Board' : 'Space Plan') + '’s saved date couldn’t be used, so it will show its last-updated date instead.' });
      }
    });
    return out;
  }

  // ==========================================================================
  // Controller (DOM)
  // ==========================================================================

  function createController(opts) {
    var container = opts.container;
    var store = window.MSGProjects;
    var state = 'IDLE';
    var inv = null;
    var planVersion = 0;
    var local = null;
    var cloud = { projects: [], homes: [] };
    var includeUnscoped = false;
    var banner = null;
    var busy = false;
    var run = null; // import run bookkeeping
    var carry = null; // results already reached in an earlier run of this session

    function marker() { return (store.getMigrationMarker && store.getMigrationMarker()) || { resolvedIds: {} }; }
    function saveMarker(m) { if (store.setMigrationMarker) store.setMigrationMarker(m); }
    function markResolved(id, status) {
      var m = marker();
      m.resolvedIds = m.resolvedIds || {};
      m.resolvedIds[id] = status;
      saveMarker(m);
      // The row is settled for the rest of this session too: it is never
      // sent again, and a Home settled as moved is linked by lookup.
      if (inv) {
        inv.homes.concat(inv.projects).forEach(function (r) {
          if (r.legacyId === id) { r.resolved = true; r.resolvedAs = status; }
        });
      }
    }

    function setLeaveWarning(on) {
      if (on) window.addEventListener('beforeunload', onBeforeUnload);
      else window.removeEventListener('beforeunload', onBeforeUnload);
    }
    function onBeforeUnload(e) { e.preventDefault(); e.returnValue = ''; return ''; }

    // ---------- scan ----------
    function scan(cloudProjects, cloudHomes) {
      cloud = { projects: cloudProjects || [], homes: cloudHomes || [] };
      local = store.readLocalForMigration ? store.readLocalForMigration() : { scoped: { projects: [], homes: [] }, unscoped: { projects: [], homes: [] } };
      var m = marker();
      includeUnscoped = m.unscopedDecision === 'include';
      inv = buildInventory(local, cloud.projects, cloud.homes, m, includeUnscoped);
      var open = openRows();
      var movable = open.filter(function (r) { return r.state !== 'not_movable'; });
      var unscopedPending = inv.hasUnscoped && !m.unscopedDecision && unscopedUnresolved() > 0;
      if (!movable.length && !unscopedPending) {
        state = open.length ? 'QUIET_INFO' : 'QUIET_NONE';
      } else if (m.deferredUntil && m.deferredUntil > timing.now()) {
        state = 'DEFERRED';
      } else {
        state = 'OFFER';
      }
      render();
      return state;
    }

    function unscopedUnresolved() {
      var cloudIds = {};
      cloud.projects.concat(cloud.homes).forEach(function (r) { if (r && r.clientLegacyId) cloudIds[r.clientLegacyId] = true; });
      var res = marker().resolvedIds || {};
      var n = 0;
      [].concat(local.unscoped.projects || [], local.unscoped.homes || []).forEach(function (r) {
        if (isObj(r) && typeof r.id === 'string' && !cloudIds[r.id.trim()] && !res[r.id.trim()]) n += 1;
      });
      return n;
    }

    function openRows() {
      return inv.homes.concat(inv.projects).filter(function (r) { return !r.resolved; });
    }

    // ---------- preview ----------
    function allOpenProjects() { return inv.projects.filter(function (p) { return !p.resolved && p.state !== 'not_movable'; }); }
    function allOpenHomes() { return inv.homes.filter(function (h) { return !h.resolved; }); }

    function startPreview() {
      state = 'PREVIEWING';
      banner = null;
      render();
      return runPreview(allOpenHomes(), allOpenProjects(), 1);
    }

    var previewsInFlight = 0;
    function runPreview(homeRows, projectRows, attemptsLeft) {
      planVersion += 1;
      var myVersion = planVersion;
      busy = true;
      previewsInFlight += 1;
      var reqs = previewRequests(inv, homeRows, projectRows, myVersion);
      var homeFromP2 = {};
      var homeFromP3 = {};
      var failure = null;

      var chain = Promise.resolve();
      reqs.forEach(function (r) {
        chain = chain.then(function () {
          if (failure) return;
          return send(r.body).then(function (out) {
            if (out.kind !== 'ok') { failure = out.kind; return; }
            if (out.data.planVersion !== myVersion || myVersion !== planVersion) return; // stale response
            if (r.kind === 'homes') {
              (out.data.homes || []).forEach(function (res, i) { applyHomePreview(r.rows[i], res); homeFromP2[res.legacyId] = res.status; });
            } else {
              (out.data.plannedHomes || []).forEach(function (res) { homeFromP3[res.legacyId] = res.status; });
              (out.data.projects || []).forEach(function (res, i) { applyProjectPreview(r.rows[i], res); });
            }
          });
        });
      });

      return chain.then(function () {
        previewsInFlight -= 1;
        busy = previewsInFlight > 0;
        if (myVersion !== planVersion) {
          // Superseded: its answers were ignored. Rows it covered stay
          // unchecked, so Move stays disabled until they are checked again.
          if (!previewsInFlight && (state === 'RECHECKING' || state === 'PREVIEWING')) { state = 'REVIEW'; render(); }
          return;
        }
        if (failure) {
          state = state === 'RECHECKING' ? 'REVIEW' : 'PREVIEW_FAILED';
          banner = failureText(failure, true);
          render();
          return;
        }
        // P6 consistency: P2 Home status must match every P3 plannedHomes status.
        var inconsistent = Object.keys(homeFromP3).filter(function (id) {
          return homeFromP2[id] !== undefined && homeFromP2[id] !== homeFromP3[id];
        });
        if (inconsistent.length) {
          if (attemptsLeft > 0) return runPreview(homeRows, projectRows, attemptsLeft - 1);
          banner = 'Your account changed while we were checking. Check again.';
          inv.homes.concat(inv.projects).forEach(function (r) { r.checkedFp = null; });
        }
        state = 'REVIEW';
        render();
      });
    }

    function applyHomePreview(h, res) {
      h.lastResult = res;
      h.checkedAt = timing.now();
      h.checkedFp = homeFingerprint(h);
      if (res.status === 'would_import') { h.state = 'ready'; h.dates = res.dates; }
      else if (res.status === 'already_imported') { h.state = 'already_in_account'; h.ticked = false; }
      else if (res.status === 'previously_deleted') { h.state = 'deleted_in_account'; h.ticked = false; }
      else if (res.status === 'invalid') {
        if (res.field === 'name') { h.state = 'needs_edit'; }
        else { h.state = 'not_movable'; h.ticked = false; h.notMovableReason = res.field; }
      }
    }

    function applyProjectPreview(p, res) {
      p.lastResult = res;
      p.checkedAt = timing.now();
      p.checkedFp = projectFingerprint(inv, p);
      if (res.status === 'would_import') { p.state = 'ready'; p.dates = res.dates; }
      else if (res.status === 'already_imported') { p.state = 'already_in_account'; p.ticked = false; }
      else if (res.status === 'previously_deleted') { p.state = 'deleted_in_account'; p.ticked = false; }
      else if (res.status === 'home_unavailable') { p.state = 'home_unavailable'; p.homeReason = res.homeReason; }
      else if (res.status === 'invalid') {
        if (res.field === 'name') { p.state = 'needs_edit'; if (p.editedName === null && !p.suggestedName && nameCheck(p.raw.name) === 'blank') p.suggestedName = suggestedProjectName(p.raw, p.room); }
        else { p.state = 'not_movable'; p.ticked = false; p.notMovableReason = res.field; }
      }
    }

    // ---------- freshness / Move enablement (§1.3, D22) ----------
    function homeFresh(h) {
      return h.state === 'ready' && h.checkedFp === homeFingerprint(h) && (timing.now() - h.checkedAt) <= PREVIEW_MAX_AGE_MS;
    }
    function projectFresh(p) {
      if (p.state !== 'ready' || p.checkedFp !== projectFingerprint(inv, p) || (timing.now() - p.checkedAt) > PREVIEW_MAX_AGE_MS) return false;
      var plan = projectHomePlan(inv, p);
      if (plan.line === 'grouped') return homeFresh(plan.homeRow);
      if (plan.line === 'waiting_home' || needsHomeChoice(inv, p)) return false;
      return true;
    }
    function tickedHomes() { return inv.homes.filter(function (h) { return !h.resolved && h.ticked; }); }
    function tickedProjects() { return inv.projects.filter(function (p) { return !p.resolved && p.ticked; }); }
    function isStale(r) {
      if (r.kind === 'home') return r.state === 'edited_unchecked' || (r.state === 'ready' && !homeFresh(r));
      // A Project waiting for a Home choice is not stale: checking again
      // can't answer it; only the customer's choice can.
      if (r.state === 'ready' && needsHomeChoice(inv, r)) return false;
      return r.state === 'edited_unchecked' || (r.state === 'ready' && !projectFresh(r));
    }
    function moveReadiness() {
      var th = tickedHomes();
      var tp = tickedProjects();
      if (!th.length && !tp.length) return { ok: false, reason: 'nothing' };
      if (tp.some(function (p) { return needsHomeChoice(inv, p); })) return { ok: false, reason: 'choice' };
      var edited = inv.homes.concat(inv.projects).some(function (r) { return r.state === 'edited_unchecked'; });
      if (edited) return { ok: false, reason: 'check' };
      var tooOld = th.concat(tp).some(function (r) { return r.state === 'ready' && r.checkedAt && (timing.now() - r.checkedAt) > PREVIEW_MAX_AGE_MS; });
      if (tooOld) return { ok: false, reason: 'stale' };
      for (var i = 0; i < th.length; i++) if (!homeFresh(th[i])) return { ok: false, reason: th[i].state === 'needs_edit' ? 'edit' : 'check' };
      for (var j = 0; j < tp.length; j++) if (!projectFresh(tp[j])) return { ok: false, reason: tp[j].state === 'needs_edit' ? 'edit' : 'check' };
      return { ok: true };
    }

    // ---------- review actions ----------
    function markStaleForHome(h) {
      inv.projects.forEach(function (p) {
        if (p.homeLegacyId === h.legacyId && p.state === 'ready') p.checkedFp = null;
      });
    }

    function onEditName(row, value) {
      row.editedName = value;
      if (nameCheck(value)) { row.state = 'needs_edit'; }
      else { row.state = 'edited_unchecked'; }
      if (row.kind === 'home') markStaleForHome(row);
      planVersion += 1; // any response to the older plan is ignored
    }

    // A ticked row waiting for a name whose field (suggestion or edit) is valid.
    function pendingEdit(r) { return r.ticked && !r.resolved && r.state === 'needs_edit' && !nameCheck(fieldValue(r)); }
    function invalidTyped(r) { return r.ticked && !r.resolved && r.state === 'needs_edit' && r.editedName !== null && !!nameCheck(r.editedName); }
    function wantsCheck(r) {
      if (r.resolved) return false;
      return r.state === 'edited_unchecked' || pendingEdit(r) || r.state === 'home_unavailable' || (r.ticked && isStale(r));
    }
    function showCheck() {
      return inv.homes.concat(inv.projects).some(function (r) { return wantsCheck(r) || (r.ticked && !r.resolved && r.state === 'needs_edit'); });
    }
    function checkEnabled() {
      if (state === 'RECHECKING' || busy) return false;
      var rows = inv.homes.concat(inv.projects);
      return rows.some(wantsCheck) && !rows.some(invalidTyped);
    }

    function checkChanges() {
      if (!checkEnabled()) return Promise.resolve();
      // The customer accepted what the field shows (suggestion or own edit).
      inv.homes.concat(inv.projects).forEach(function (r) {
        if (pendingEdit(r)) { r.editedName = fieldValue(r); r.state = 'edited_unchecked'; }
      });
      var homes = inv.homes.filter(function (h) { return h.state === 'edited_unchecked' || (h.ticked && h.state === 'ready' && !homeFresh(h)); });
      var homeIds = {};
      homes.forEach(function (h) { homeIds[h.legacyId] = true; });
      var projects = inv.projects.filter(function (p) {
        if (p.resolved || p.state === 'not_movable' || p.state === 'already_in_account' || p.state === 'deleted_in_account') return false;
        if (p.state === 'edited_unchecked' || p.state === 'home_unavailable') return true;
        if (homeIds[p.homeLegacyId]) return true;
        return p.ticked && p.state === 'ready' && !projectFresh(p);
      });
      state = 'RECHECKING';
      banner = null;
      render();
      return runPreview(homes, projects, 1);
    }

    function toggleRow(row, on) {
      row.ticked = !!on;
      if (row.kind === 'home') markStaleForHome(row);
      planVersion += 1;
    }

    // ---------- import ----------
    function startImport() {
      var ready = moveReadiness();
      if (!ready.ok) {
        // The last check is over 10 minutes old: check everything again first.
        if (ready.reason === 'stale') return onAction('check-all');
        render();
        return Promise.resolve();
      }
      state = 'MOVING_HOMES';
      setLeaveWarning(true);
      run = {
        homesTodo: tickedHomes().filter(function (h) { return h.state === 'ready'; }),
        projectsTodo: tickedProjects(),
        moved: carry ? carry.moved : [], deleted: carry ? carry.deleted : [], failed: carry ? carry.failed : [],
        changed: carry ? carry.changed : [], kept: carry ? carry.kept : [], blockedHomes: [], unavailable: [],
        step: 0, steps: 0
      };
      carry = null;
      // Defensive (Fix A): a Project still waiting for a Home choice never
      // reaches a real import, whatever the UI state.
      run.projectsTodo = run.projectsTodo.filter(function (p) { return !needsHomeChoice(inv, p); });
      // Fix D: records the preview found deleted in the account were shown
      // on the review; once the customer moves, they are settled for good.
      settleDeleted();
      run.homeReqs = importHomeRequests(run.homesTodo);
      run.projectReqs = importProjectRequests(inv, run.projectsTodo);
      run.steps = run.homeReqs.length + run.projectReqs.length;
      render();
      return nextHomeBatch();
    }

    function nextHomeBatch() {
      if (!run.homeReqs.length) { state = 'MOVING_PROJECTS'; render(); return nextProjectBatch(); }
      var r = run.homeReqs[0];
      run.step += 1;
      run.lastCheck = null;
      render();
      return send(r.body).then(function (out) {
        if (out.kind === 'ok') return afterHomeBatch(r, out.data.homes || []);
        if (out.kind === 'unknown') return checkHomeBatch(r);
        if (out.kind === 'offline') return pauseUnsent();
        return stopWith(out.kind);
      });
    }

    function afterHomeBatch(r, results) {
      var blocked = [];
      results.forEach(function (res, i) {
        var h = r.rows[i];
        if (res.status === 'imported' || res.status === 'already_imported') {
          h.state = 'moved'; h.movedId = res.id; markResolved(h.legacyId, 'moved'); run.moved.push(h);
        } else {
          h.state = res.status === 'previously_deleted' ? 'deleted_in_account' : 'failed_changed';
          if (res.status === 'previously_deleted') markResolved(h.legacyId, 'deleted_in_account');
          h.blockReason = res.status === 'previously_deleted' ? 'deleted' : 'invalid';
          h.lastResult = res;
          blocked.push(h);
        }
      });
      run.homeReqs.shift();
      if (blocked.length) return homeBlocked(blocked);
      return nextHomeBatch();
    }

    // Dry-run check of what reached the account (Fix C: every request the
    // rows split into is sent and every row is accounted for). Results are
    // matched by legacyId, never by position. Stops at the first request that
    // fails; rows without a result stay unverified.
    function dryRunCheck(homeRows, projectRows) {
      var reqs = previewRequests(inv, homeRows, projectRows, 'check');
      var results = {};
      var failure = null;
      var sent = 0;
      var chain = Promise.resolve();
      reqs.forEach(function (r) {
        chain = chain.then(function () {
          if (failure) return;
          sent += 1;
          return send(r.body).then(function (out) {
            if (out.kind !== 'ok') { failure = out.kind; return; }
            var want = {};
            r.rows.forEach(function (row) { want[row.legacyId] = true; });
            var list = r.kind === 'homes' ? out.data.homes : out.data.projects;
            (Array.isArray(list) ? list : []).forEach(function (res) {
              if (isObj(res) && want[res.legacyId] && !results[res.legacyId]) results[res.legacyId] = res;
            });
          });
        });
      });
      return chain.then(function () {
        if (run) run.checkRequests = (run.checkRequests || 0) + sent;
        return { failure: failure, results: results, requests: reqs.length, sent: sent };
      });
    }

    function pauseRun(reason) {
      state = 'PAUSED';
      run.pauseReason = reason;
      setLeaveWarning(false);
      render();
    }
    // #16: the phone was offline before the step started, so nothing was
    // sent: the step stays first in line and Continue sends it (it cannot
    // have reached the account, so this is never a blind resend).
    function pauseUnsent() {
      run.step = Math.max(0, run.step - 1);
      run.pendingCheck = null;
      return pauseRun('offline');
    }
    function dropHead(list, r) { if (list[0] === r) list.shift(); }

    // Unknown outcome on a Home batch: dry-run the same Homes to see what landed.
    function checkHomeBatch(r) {
      state = 'CHECKING';
      render();
      var rows = r.rows.filter(function (h) { return !h.resolved; });
      return dryRunCheck(rows, []).then(function (chk) {
        dropHead(run.homeReqs, r);
        var landed = [];
        var deleted = [];
        var notLanded = [];
        var unverified = [];
        rows.forEach(function (h) {
          var res = chk.results[h.legacyId];
          if (!res) { unverified.push(h); return; }
          if (res.status === 'already_imported') { h.state = 'moved'; h.movedId = res.id; markResolved(h.legacyId, 'moved'); run.moved.push(h); landed.push(h); }
          else if (res.status === 'previously_deleted') {
            // Fix D: authoritative — settle it now, whatever happens next.
            h.state = 'deleted_in_account'; h.blockReason = 'deleted'; markResolved(h.legacyId, 'deleted_in_account');
            if (run.deleted.indexOf(h) === -1) run.deleted.push(h);
            deleted.push(h);
          } else { h.blockReason = res.status === 'invalid' ? 'invalid' : 'unreached'; notLanded.push(h); }
        });
        run.lastCheck = { kind: 'homes', total: rows.length, landed: landed.length, deleted: deleted.length, notLanded: notLanded.length, unverified: unverified.length };
        if (unverified.length) {
          // Still unknown for some Homes: Continue checks them again first.
          // Nothing is resent until every Home in the step is accounted for.
          var again = unverified.concat(notLanded);
          run.pendingCheck = { kind: 'homes', rows: again };
          return pauseRun(chk.failure || 'unknown');
        }
        var blocked = deleted.concat(notLanded);
        if (blocked.length) {
          // Not landed: resending needs the customer's action (Try again).
          if (notLanded.length) run.homeReqs.unshift({ kind: 'homes', rows: notLanded, body: { homes: notLanded.map(homePayload), projects: [] } });
          return homeBlocked(blocked, true);
        }
        state = 'MOVING_HOMES';
        return nextHomeBatch();
      });
    }

    function homeBlocked(homes, requeued) {
      run.blockedHomes = homes;
      run.blockedRequeued = !!requeued;
      state = 'HOME_BLOCKED';
      setLeaveWarning(false);
      render();
    }

    function nextProjectBatch() {
      if (!run.projectReqs.length) return finish();
      var r = run.projectReqs[0];
      run.step += 1;
      run.lastCheck = null;
      render();
      return send(r.body).then(function (out) {
        if (out.kind === 'ok') return afterProjectBatch(r, out.data.projects || []);
        if (out.kind === 'unknown') return checkProjectBatch(r);
        if (out.kind === 'offline') return pauseUnsent();
        return stopWith(out.kind);
      });
    }

    function afterProjectBatch(r, results) {
      var unavailable = [];
      results.forEach(function (res, i) {
        var p = r.rows[i];
        applyProjectImportResult(p, res, unavailable);
      });
      run.projectReqs.shift();
      if (unavailable.length) {
        var homes = [];
        var seen = {};
        unavailable.forEach(function (p) {
          var h = homeById(inv, p.homeLegacyId);
          if (h && !seen[h.legacyId]) { seen[h.legacyId] = true; h.blockReason = p.homeReason === 'deleted' ? 'deleted' : 'unreached'; homes.push(h); }
        });
        run.unavailable = run.unavailable.concat(unavailable);
        return homeBlocked(homes);
      }
      return nextProjectBatch();
    }

    function applyProjectImportResult(p, res, unavailable) {
      if (res.status === 'imported' || res.status === 'already_imported') {
        p.state = 'moved'; markResolved(p.legacyId, 'moved'); run.moved.push(p);
        if (p.checkedFp && p.lastResult && p.lastResult.status === 'would_import' && res.status === 'already_imported') run.changed.push({ row: p, text: 'was already in your account (moved from another tab or device), so no copy was made' });
      } else if (res.status === 'previously_deleted') {
        p.state = 'deleted_in_account'; markResolved(p.legacyId, 'deleted_in_account'); run.deleted.push(p);
        run.changed.push({ row: p, text: 'was deleted from your account while you were reviewing, so it wasn’t added back. It’s still in this browser.' });
      } else if (res.status === 'home_unavailable') {
        p.state = 'home_blocked'; p.homeReason = res.homeReason; unavailable.push(p);
      } else {
        p.state = 'failed_changed'; p.lastResult = res; run.failed.push(p);
        run.changed.push({ row: p, text: 'couldn’t be moved (' + fieldText(res.field) + '). It’s still in this browser.' });
      }
    }

    // Unknown outcome on a Project batch (lost connection, unreadable answer,
    // server error): check EVERY row of the step — across every request the
    // check splits into (Fix C) — and report what actually landed (Fix B).
    function checkProjectBatch(r) {
      state = 'CHECKING';
      render();
      var rows = r.rows.filter(function (p) { return !p.resolved; });
      return dryRunCheck([], rows).then(function (chk) {
        dropHead(run.projectReqs, r);
        var landed = [];
        var deleted = [];
        var notLanded = [];
        var unverified = [];
        rows.forEach(function (p) {
          var res = chk.results[p.legacyId];
          if (!res) { unverified.push(p); return; }
          if (res.status === 'already_imported') { p.state = 'moved'; markResolved(p.legacyId, 'moved'); run.moved.push(p); landed.push(p); }
          else if (res.status === 'previously_deleted') {
            p.state = 'deleted_in_account'; markResolved(p.legacyId, 'deleted_in_account'); run.deleted.push(p); deleted.push(p);
            run.changed.push({ row: p, text: 'was deleted from your account while you were reviewing, so it wasn’t added back. It’s still in this browser.' });
          } else notLanded.push(p);
        });
        run.lastCheck = { kind: 'projects', total: rows.length, landed: landed.length, deleted: deleted.length, notLanded: notLanded.length, unverified: unverified.length };
        // Rows the check confirmed did NOT land wait for Continue; rows it
        // couldn't confirm are checked again first (never resent blindly).
        if (notLanded.length) run.projectReqs = importProjectRequests(inv, notLanded).concat(run.projectReqs);
        if (unverified.length) {
          run.pendingCheck = { kind: 'projects', rows: unverified };
          return pauseRun(chk.failure || 'unknown');
        }
        if (notLanded.length) return pauseRun('unknown');
        state = 'MOVING_PROJECTS';
        return nextProjectBatch();
      });
    }

    function continueAfterPause() {
      if (!run) return Promise.resolve();
      setLeaveWarning(true);
      if (run.pendingCheck) {
        // The outcome of that batch is still unknown: check it, never resend it blindly.
        var r = run.pendingCheck;
        run.pendingCheck = null;
        return r.kind === 'homes' ? checkHomeBatch(r) : checkProjectBatch(r);
      }
      state = run.homeReqs.length ? 'MOVING_HOMES' : 'MOVING_PROJECTS';
      render();
      return run.homeReqs.length ? nextHomeBatch() : nextProjectBatch();
    }

    // Home blocked recovery (D19)
    function blockedTryAgain() {
      var homes = run.blockedHomes || [];
      state = 'CHECKING';
      run.lastCheck = null;
      render();
      return dryRunCheck(homes, []).then(function (chk) {
        if (chk.failure) {
          // Fix D: anything the check DID confirm deleted is settled anyway.
          homes.forEach(function (h) {
            var res = chk.results[h.legacyId];
            if (res && res.status === 'previously_deleted') settleDeletedRow(h);
          });
          state = 'HOME_BLOCKED'; banner = failureText(chk.failure, false); render(); return;
        }
        var stillBlocked = [];
        var toSend = [];
        homes.forEach(function (h) {
          var res = chk.results[h.legacyId];
          if (!res) { h.blockReason = 'unreached'; stillBlocked.push(h); return; }
          if (res.status === 'already_imported') { h.state = 'moved'; markResolved(h.legacyId, 'moved'); if (run.moved.indexOf(h) === -1) run.moved.push(h); }
          else if (res.status === 'would_import') toSend.push(h);
          else if (res.status === 'previously_deleted') { h.blockReason = 'deleted'; settleDeletedRow(h); stillBlocked.push(h); }
          else { h.blockReason = 'invalid'; stillBlocked.push(h); }
        });
        run.blockedHomes = [];
        if (stillBlocked.length) { run.blockedHomes = stillBlocked; state = 'HOME_BLOCKED'; render(); return; }
        // Re-queue Projects that came back home_unavailable for these Homes.
        if (run.unavailable.length) {
          run.projectReqs = importProjectRequests(inv, run.unavailable).concat(run.projectReqs);
          run.unavailable = [];
        }
        setLeaveWarning(true);
        run.homeReqs = toSend.length ? importHomeRequests(toSend).concat(run.homeReqs.filter(function (r) { return r.rows.every(function (h) { return toSend.indexOf(h) === -1; }); })) : run.homeReqs.filter(function (r) { return r.rows.every(function (h) { return h.state !== 'moved'; }); });
        state = run.homeReqs.length ? 'MOVING_HOMES' : 'MOVING_PROJECTS';
        render();
        return run.homeReqs.length ? nextHomeBatch() : nextProjectBatch();
      });
    }

    function blockedMoveWithout() {
      var homeIds = {};
      (run.blockedHomes || []).forEach(function (h) { homeIds[h.legacyId] = true; });
      inv.projects.forEach(function (p) {
        if (homeIds[p.homeLegacyId] && p.state !== 'moved') {
          p.withoutHome = true;
          if (p.state === 'home_blocked' || p.state === 'home_unavailable') p.state = 'ready';
          p.checkedFp = null; // must be checked and confirmed again
        }
      });
      carry = { moved: run.moved, deleted: run.deleted, failed: run.failed, changed: run.changed, kept: run.kept };
      run = null;
      state = 'REVIEW';
      banner = 'These Projects will now move without a Home. Check them, then confirm again.';
      render();
    }

    // Keep: the blocked Homes' Projects stay in this browser; everything
    // else in this move carries on.
    function blockedKeep() {
      var ids = {};
      (run.blockedHomes || []).forEach(function (h) { ids[h.legacyId] = true; });
      function affected(p) { return !!ids[p.homeLegacyId] && projectHomePlan(inv, p).homeId !== null; }
      var remainingProjects = [];
      run.projectReqs.forEach(function (r) {
        r.rows.forEach(function (p) {
          if (p.state === 'moved' || p.state === 'deleted_in_account') return;
          if (affected(p)) { if (run.kept.indexOf(p) === -1) run.kept.push(p); }
          else remainingProjects.push(p);
        });
      });
      run.unavailable.forEach(function (p) { if (run.kept.indexOf(p) === -1) run.kept.push(p); });
      run.unavailable = [];
      var remainingHomes = [];
      run.homeReqs.forEach(function (r) { r.rows.forEach(function (h) { if (!ids[h.legacyId] && h.state !== 'moved') remainingHomes.push(h); }); });
      run.blockedHomes = [];
      run.homeReqs = importHomeRequests(remainingHomes);
      run.projectReqs = importProjectRequests(inv, remainingProjects);
      run.steps = run.step + run.homeReqs.length + run.projectReqs.length;
      if (!run.homeReqs.length && !run.projectReqs.length) return finish();
      setLeaveWarning(true);
      state = run.homeReqs.length ? 'MOVING_HOMES' : 'MOVING_PROJECTS';
      render();
      return run.homeReqs.length ? nextHomeBatch() : nextProjectBatch();
    }

    function stopWith(kind) {
      setLeaveWarning(false);
      if (kind === 'rate_limited') {
        state = 'PAUSED';
        run.pauseReason = kind;
        render();
        return;
      }
      // Fix D: this run ends here, so deleted records are settled too.
      settleDeleted();
      state = 'RESULTS';
      run.stopReason = kind;
      render();
    }

    // Fix D: an authoritative previously_deleted is recorded as resolved
    // (ids and statuses only), so the same deleted record is never offered
    // again. Called on every exit path of a run and when the customer leaves
    // the review with "Not now".
    function settleDeletedRow(r) {
      if (run && run.deleted.indexOf(r) === -1) run.deleted.push(r);
      r.state = 'deleted_in_account';
      if (!(r.resolved && r.resolvedAs === 'deleted_in_account')) markResolved(r.legacyId, 'deleted_in_account');
    }
    function settleDeleted() {
      if (!inv) return;
      inv.homes.concat(inv.projects).forEach(function (r) {
        if (!r.resolved && r.state === 'deleted_in_account') settleDeletedRow(r);
      });
    }

    function finish() {
      setLeaveWarning(false);
      state = 'RESULTS';
      // Records found deleted from the account are reported on this screen
      // and then never offered again (ids and statuses only).
      settleDeleted();
      render();
    }

    // #17: "Stop here" from a pause. A step whose outcome is still unknown
    // stays unknown: it is NOT recorded as moved or as not moved, so the next
    // visit reads the account first and offers only what isn't there yet.
    function stopByCustomer() {
      run.stoppedByCustomer = true;
      run.uncertain = run.pendingCheck ? run.pendingCheck.rows.filter(function (r) { return r.state !== 'moved'; }) : [];
      run.pendingCheck = null;
      return finish();
    }

    function done() {
      run = null;
      if (typeof opts.onDone === 'function') opts.onDone();
    }

    // ---------- deferral ----------
    function defer() {
      settleDeleted();
      var m = marker();
      m.deferredUntil = timing.now() + DEFER_MS;
      saveMarker(m);
      state = 'DEFERRED';
      render();
    }

    function decideUnscoped(include) {
      var fromReview = state === 'REVIEW';
      var m = marker();
      m.unscopedDecision = include ? 'include' : 'exclude';
      saveMarker(m);
      var s = scan(cloud.projects, cloud.homes);
      // Answered on the review: rebuild the plan and check it again.
      if (fromReview && s === 'OFFER') return startPreview();
    }

    function failureText(kind, preview) {
      // Polish #11: a failed preview is a check, not a move.
      if (kind === 'unavailable') return preview
        ? 'We couldn’t check your Projects right now. They’re still in this browser.'
        : 'We couldn’t move your Projects right now. They’re still in this browser.';
      if (kind === 'not_entitled') return 'Moving Projects to your account is part of Môdern Studio™ AI+. Your Projects are still in this browser.';
      if (kind === 'signed_out') return 'Please sign in again to move your Projects. They’re still in this browser.';
      if (kind === 'rate_limited') return 'Please wait a moment, then try again. Your Projects are still in this browser.';
      if (kind === 'offline') return preview
        ? 'You’re offline. Connect to the internet, then try again. Your Projects are still in this browser.'
        : 'You’re offline, so nothing was sent. Your Projects are still in this browser.';
      if (preview) return 'We couldn’t check your Projects just now. Please try again. They’re still in this browser.';
      return 'We couldn’t reach your account just now. Your Projects are still in this browser.';
    }

    // ==========================================================================
    // Rendering
    // ==========================================================================

    function homeName(h) { var n = trimmed(currentName(h)); return n || '(unnamed Home)'; }
    // P4 (D5): a Home that is already in the account is shown by its CURRENT
    // account name (it may have been renamed there since this browser copy
    // was saved). Display only: the Home is still linked by its browser ID,
    // exactly as before. Falls back to the browser name when the account
    // list doesn't include it.
    function accountHomeName(legacyId) {
      var list = (cloud && cloud.homes) || [];
      for (var i = 0; i < list.length; i++) {
        var c = list[i];
        if (c && c.clientLegacyId === legacyId) { var n = trimmed(str(c.name)); if (n) return n; }
      }
      return null;
    }
    function shownHomeName(h) { return (h && accountHomeName(h.legacyId)) || homeName(h); }
    function outsideName(o) { var n = o ? trimmed(o.name) : ''; return n || '(unnamed Home)'; }
    function projectName(p) { var n = trimmed(currentName(p)); return n || '(unnamed Project)'; }

    function projectHomeLine(p) {
      var plan = projectHomePlan(inv, p);
      var h = plan.homeRow;
      var lines = [];
      var hn = h ? q(homeName(h)) : '';
      switch (plan.line) {
        case 'grouped': lines.push('Will be grouped under ' + hn + '.'); break;
        case 'already_in_account': lines.push('Will be grouped under ' + q(shownHomeName(h)) + ' (already in your account).'); break;
        case 'without_unticked': lines.push('Will move without a Home — you’re keeping ' + hn + ' in this browser.'); break;
        case 'without_not_movable': lines.push('Will move without a Home — ' + hn + ' can’t be moved (' + fieldText(h.notMovableReason) + ').'); break;
        case 'without_deleted': lines.push('Will move without a Home — ' + hn + ' was deleted from your account earlier and won’t be added back.'); break;
        case 'without_chosen': lines.push('Will move without a Home, as you chose.'); break;
        case 'waiting_home': lines.push('Waiting for ' + hn + ' to be named.'); break;
        case 'ask_deleted': lines.push(hn + ' was deleted from your account earlier. Move this Project without a Home, or keep it in this browser?'); break;
        case 'outside_in_account': lines.push('Will be grouped under ' + q(accountHomeName(plan.outside.legacyId) || outsideName(plan.outside)) + ' (already in your account).'); break;
        case 'ask_outside':
          lines.push('Listed in ' + q(outsideName(plan.outside)) + ', a Home saved in this browser before sign-in' +
            (plan.outside.decision === 'exclude'
              ? ', which you chose to leave out. That Home stays in this browser.'
              : '. That Home isn’t part of this move unless you confirm the items saved before sign-in are yours.'));
          lines.push('Move this Project without a Home, or keep it in this browser?');
          break;
        case 'without_outside': lines.push('Will move without a Home — ' + q(outsideName(plan.outside)) + ' (saved before sign-in) stays in this browser, as you chose.'); break;
        default: break;
      }
      if (p.alsoInHomes.length && plan.homeRow) {
        var other = homeById(inv, p.alsoInHomes[0]);
        if (other) lines.push('Also listed in ' + q(homeName(other)) + '; it will be grouped under ' + hn + '.');
      }
      return { lines: lines, ask: plan.line === 'ask_deleted' || plan.line === 'ask_outside' };
    }

    function render() {
      if (!container) return;
      var html = '';
      if (state === 'QUIET_NONE' || state === 'IDLE') { container.hidden = true; container.innerHTML = ''; return; }
      container.hidden = false;
      if (state === 'QUIET_INFO' || state === 'DEFERRED') html = renderQuiet();
      else if (state === 'OFFER') html = renderOffer();
      else if (state === 'PREVIEWING') html = card('<p class="mp-migrate__status" role="status">Checking your Projects with your account…</p>');
      else if (state === 'PREVIEW_FAILED') html = card('<p class="mp-migrate__status">' + esc(banner) + '</p>' + actions([['review', 'Try again', true], ['not-now', 'Not now']]));
      else if (state === 'REVIEW' || state === 'RECHECKING') html = renderReview();
      else if (state === 'MOVING_HOMES' || state === 'MOVING_PROJECTS' || state === 'CHECKING') html = renderMoving();
      else if (state === 'HOME_BLOCKED') html = renderBlocked();
      else if (state === 'PAUSED') html = renderPaused();
      else if (state === 'RESULTS') html = renderResults();
      container.innerHTML = html;
      bind();
    }

    function card(inner) { return '<div class="mp-migrate" data-migration-state="' + state + '">' + inner + '</div>'; }
    function actions(list) {
      return '<div class="mp-migrate__actions">' + list.map(function (a) {
        return '<button type="button" class="button ' + (a[2] ? 'button--primary' : 'button--secondary') + '" data-migrate-action="' + a[0] + '"' + (a[3] ? ' disabled' : '') + '>' + esc(a[1]) + '</button>';
      }).join('') + '</div>';
    }

    function counts() {
      var p = inv.projects.filter(function (r) { return !r.resolved && r.state !== 'not_movable'; }).length;
      var h = inv.homes.filter(function (r) { return !r.resolved; }).length;
      var bad = inv.projects.filter(function (r) { return !r.resolved && r.state === 'not_movable'; }).length;
      return { projects: p, homes: h, notMovable: bad };
    }

    function renderQuiet() {
      var c = counts();
      if (state === 'QUIET_INFO') {
        return '<p class="mp-migrate-quiet" data-migration-state="QUIET_INFO">' + esc(plural(c.notMovable, 'Project', 'Projects') + ' can’t be moved — ' + (c.notMovable === 1 ? 'it’s' : 'they’re') + ' still in this browser.') + ' <button type="button" class="mp-migrate-link" data-migrate-action="review">Review</button></p>';
      }
      // Polish #10: mention Homes too when both are waiting.
      var what = c.projects && c.homes
        ? plural(c.projects, 'Project', 'Projects') + ' and ' + plural(c.homes, 'Home', 'Homes') + ' are'
        : c.projects ? plural(c.projects, 'Project is', 'Projects are') : (c.homes ? plural(c.homes, 'Home is', 'Homes are') : 'Some items are');
      return '<p class="mp-migrate-quiet" data-migration-state="DEFERRED">' + esc(what + ' saved only in this browser —') + ' <button type="button" class="mp-migrate-link" data-migrate-action="review">Review</button></p>';
    }

    function renderOffer() {
      var c = counts();
      var m = marker();
      var needUnscoped = inv.hasUnscoped && !m.unscopedDecision && unscopedUnresolved() > 0;
      var list = inv.projects.filter(function (p) { return !p.resolved && p.state !== 'not_movable'; }).slice(0, 5).map(function (p) {
        return '<li>' + esc(projectName(p)) + ' — ' + esc(summaryOf(p)) + '</li>';
      }).join('');
      var homes = inv.homes.filter(function (h) { return !h.resolved; }).slice(0, 5).map(function (h) {
        return '<li>Home: ' + esc(q(homeName(h))) + '</li>';
      }).join('');
      // Polish #8: Homes first, then Projects; each overflow line says what it counts.
      var moreProjects = c.projects > 5 ? '<li>and ' + esc(plural(c.projects - 5, 'more Project', 'more Projects')) + '</li>' : '';
      var moreHomes = c.homes > 5 ? '<li>and ' + esc(plural(c.homes - 5, 'more Home', 'more Homes')) + '</li>' : '';
      var inner = '<h2 class="mp-migrate__title">Move your Projects to your Môdern Studio™ account</h2>';
      if (c.projects || c.homes) {
        inner += '<p>We found <strong>' + esc(plural(c.projects, 'Project', 'Projects')) + (c.homes ? ' and ' + esc(plural(c.homes, 'Home', 'Homes')) : '') + '</strong> saved only in this browser. Moving them keeps them with your account, so you can open them on any device where you’re signed in.</p>' +
          '<ul class="mp-migrate__list">' + homes + moreHomes + list + moreProjects + '</ul>';
      }
      if (c.notMovable) inner += '<p class="mp-migrate__note">' + esc(plural(c.notMovable, 'Project', 'Projects')) + ' can’t be moved yet; ' + (c.notMovable === 1 ? 'it stays' : 'they stay') + ' in this browser.</p>';
      if (needUnscoped) inner += unscopedBlock();
      inner += '<p class="mp-migrate__note">A copy stays in this browser. Nothing in your account is replaced.</p>';
      inner += actions([['review', 'Review and move', true, !(c.projects || c.homes)], ['not-now', 'Not now']]);
      return card(inner);
    }

    // The existing "If they're yours" ownership confirmation (unchanged
    // wording), also shown on the review while it is still unanswered.
    function unscopedBlock() {
      var m = marker();
      if (!(inv.hasUnscoped && !m.unscopedDecision && unscopedUnresolved() > 0)) return '';
      return '<div class="mp-migrate__unscoped" data-migrate-unscoped><p><strong>We also found ' + esc(plural(unscopedUnresolved(), 'item', 'items')) + ' saved in this browser before Projects were linked to accounts.</strong> Include them only if they’re yours.</p>' +
        actions([['include-unscoped', 'They’re mine — include them'], ['exclude-unscoped', 'Leave them out']]) + '</div>';
    }

    function rowNotes(r) {
      var notes = [];
      if (r.kind === 'project') {
        var hl = projectHomeLine(r);
        hl.lines.forEach(function (t) { notes.push('<p class="mp-migrate__row-note">' + esc(t) + '</p>'); });
        if (hl.ask) notes.push('<div class="mp-migrate__row-actions"><button type="button" class="button button--secondary" data-migrate-row="without-home" data-id="' + esc(r.legacyId) + '">Move without a Home</button> <button type="button" class="button button--secondary" data-migrate-row="keep" data-id="' + esc(r.legacyId) + '">Keep in this browser</button></div>');
        if (r.roomLabelReplaced) notes.push('<p class="mp-migrate__row-note">Room label will show as ' + esc(q(ROOM_LABELS[r.room] || 'the room name')) + '.</p>');
      }
      if (r.state === 'ready' && r.editedName !== null && trimmed(r.editedName) !== trimmed(r.raw.name)) {
        notes.push('<p class="mp-migrate__row-note mp-migrate__row-note--change">Will be saved as ' + esc(q(trimmed(r.editedName))) + '.</p>');
      }
      if (r.state === 'ready' && r.dates) {
        dateNotes(r.dates, r.kind === 'home' ? 'Home' : 'Project').forEach(function (n) {
          notes.push('<p class="mp-migrate__row-note">' + esc(n.text) + (n.optIn ? ' <button type="button" class="mp-migrate-link" data-migrate-row="use-move-date" data-kind="' + r.kind + '" data-id="' + esc(r.legacyId) + '">Use the date it’s moved instead</button>' : '') + '</p>');
        });
      }
      if (r.state === 'already_in_account') notes.push('<p class="mp-migrate__row-note">Already in your account' + (r.kind === 'project' ? ' (not changed). You can change its Home in My Projects.' : '.') + '</p>');
      if (r.state === 'deleted_in_account') notes.push('<p class="mp-migrate__row-note">Moved earlier and deleted from your account. It won’t be added back.</p>');
      if (r.state === 'not_movable') notes.push('<p class="mp-migrate__row-note">Can’t be moved: ' + esc(fieldText(r.notMovableReason)) + '. It stays in this browser.</p>');
      if (r.state === 'home_unavailable' && r.homeReason === 'not_found') notes.push('<p class="mp-migrate__row-note">Its Home couldn’t be found. Check again, or move it without a Home.</p>');
      return notes.join('');
    }

    function rowHtml(r) {
      var id = esc(r.legacyId);
      var editable = r.state === 'needs_edit' || r.state === 'edited_unchecked' || (r.editedName !== null && r.state === 'ready');
      var tickable = ['ready', 'candidate', 'needs_edit', 'edited_unchecked', 'home_unavailable'].indexOf(r.state) !== -1;
      var canTick = r.state === 'ready';
      var title = r.kind === 'home' ? 'Home: ' + (r.state === 'already_in_account' ? shownHomeName(r) : homeName(r)) : projectName(r);
      var sub = r.kind === 'project' ? summaryOf(r) : plural(inv.projects.filter(function (p) { return p.homeLegacyId === r.legacyId; }).length, 'Project', 'Projects');
      var h = '<li class="mp-migrate__row" data-migrate-row-id="' + id + '" data-row-kind="' + r.kind + '" data-row-state="' + r.state + '">';
      var locked = state === 'RECHECKING';
      h += '<label class="mp-migrate__row-main"><input type="checkbox" data-migrate-tick="' + r.kind + '" data-id="' + id + '"' + (r.ticked && tickable ? ' checked' : '') + (!locked && (canTick || (r.ticked && tickable)) ? '' : ' disabled') + '>' +
        '<span class="mp-migrate__row-title">' + esc(title) + '</span><span class="mp-migrate__row-sub">' + esc(sub) + '</span></label>';
      if (editable) {
        var val = fieldValue(r);
        var len = typeof val === 'string' ? val.length : 0;
        h += '<div class="mp-migrate__edit"><label><span data-migrate-prompt="' + id + '">' + esc(namePrompt(r, val)) + '</span> <input type="text" class="mp-project-name-input" data-migrate-edit="' + r.kind + '" data-id="' + id + '" value="' + esc(val) + '"' + (locked ? ' disabled' : '') + '></label>' +
          '<span class="mp-migrate__count" data-migrate-count="' + id + '">' + len + ' / ' + MAX_NAME + '</span></div>';
        if (r.state !== 'ready') h += '<p class="mp-migrate__row-note">Check your change to include this ' + (r.kind === 'home' ? 'Home' : 'Project') + '.</p>';
      }
      h += rowNotes(r);
      return h + '</li>';
    }

    // #14: the prompt describes the name as it is NOW (shortened, cleared or
    // still too long), not the reason the row first needed a change.
    function namePrompt(r, val) {
      if (r.state === 'ready') return 'Name:';
      var c = nameCheck(val);
      if (c === 'too_long') return 'This name is too long for your account (' + val.length + ' characters). Shorten it:';
      if (c === 'blank' || (r.editedName === null && r.editReason === 'blank')) {
        return r.kind === 'home' ? 'This Home has no name. Name it:' : 'This Project has no name. Name it:';
      }
      return 'Name:';
    }

    function renderReview() {
      var shownHomes = inv.homes.filter(function (h) { return !h.resolved; });
      var shownProjects = inv.projects.filter(function (p) { return !p.resolved; });
      var ready = moveReadiness();
      var tp = tickedProjects();
      var th = tickedHomes();
      var withoutHome = tp.filter(function (p) { var l = projectHomePlan(inv, p).line; return l.indexOf('without') === 0; }).length;
      // #15: only a change that has been checked (row ready) counts as approved;
      // one still waiting is already counted in "needs a change checked first".
      var changes = tp.concat(th).filter(function (r) { return r.state === 'ready' && r.editedName !== null && trimmed(r.editedName) !== trimmed(r.raw.name); }).length;
      var anyEdited = showCheck();
      var inner = '<h2 class="mp-migrate__title">Review before moving</h2>';
      if (banner) inner += '<p class="mp-migrate__banner" role="status">' + esc(banner) + '</p>';
      inner += unscopedBlock();
      if (state === 'RECHECKING') inner += '<p class="mp-migrate__status" role="status">Checking your changes…</p>';
      if (shownHomes.length) inner += '<h3 class="mp-migrate__subtitle">Homes</h3><ul class="mp-migrate__rows">' + shownHomes.map(rowHtml).join('') + '</ul>';
      inner += '<h3 class="mp-migrate__subtitle">Projects</h3><ul class="mp-migrate__rows">' + shownProjects.map(rowHtml).join('') + '</ul>';
      // Count only rows that are checked and ready; rows still waiting on the
      // customer are counted separately, so the summary never over-promises.
      var choice = tp.filter(function (r) { return r.state === 'ready' && needsHomeChoice(inv, r); }).length;
      var readyP = tp.filter(function (r) { return r.state === 'ready'; }).length - choice;
      var readyH = th.filter(function (r) { return r.state === 'ready'; }).length;
      var waiting = tp.concat(th).length - readyP - readyH - choice;
      var summary = plural(readyP, 'Project', 'Projects') + ' and ' + plural(readyH, 'Home', 'Homes') + ' ready to move.';
      if (choice) summary += choice === 1 ? ' 1 more needs your choice about its Home.' : ' ' + choice + ' more need your choice about their Home.';
      if (waiting) summary += waiting === 1 ? ' 1 more needs a change checked first.' : ' ' + waiting + ' more need changes checked first.';
      if (withoutHome) summary += ' ' + plural(withoutHome, 'Project', 'Projects') + ' will move without a Home.';
      if (changes) summary += ' ' + plural(changes, 'has a change', 'have changes') + ' you approved.';
      inner += '<p class="mp-migrate__summary">' + esc(summary) + '</p>';
      inner += '<p class="mp-migrate__note">A copy stays in this browser. Dates that show “the day it’s moved” use the time of the move itself, which may differ slightly from this preview.</p>';
      var moveLabel = ready.reason === 'stale' ? 'Check again' : 'Move to my account';
      var moveAction = ready.reason === 'stale' ? 'check-all' : 'move';
      var list = [];
      if (anyEdited && ready.reason !== 'stale') list.push(['check-changes', 'Check changes', !ready.ok, !checkEnabled()]);
      list.push([moveAction, moveLabel, true, state === 'RECHECKING' || busy || (moveAction === 'move' && !ready.ok)]);
      list.push(['cancel', 'Not now']);
      inner += actions(list);
      return card(inner);
    }

    function renderMoving() {
      var text = state === 'CHECKING'
        ? 'Checking what reached your account…'
        : 'Moving… step ' + Math.max(1, run ? run.step : 1) + ' of ' + (run ? run.steps : 1) + ' — please keep this page open.';
      return card('<p class="mp-migrate__status" role="status">' + esc(text) + '</p>');
    }

    function renderBlocked() {
      var homes = run.blockedHomes || [];
      var names = homes.map(function (h) { return q(homeName(h)); }).join(', ');
      var reason = homes[0] && homes[0].blockReason === 'deleted' ? 'it was deleted there'
        : homes[0] && homes[0].blockReason === 'invalid' ? 'its name can’t be used'
          : 'we couldn’t reach your account';
      var ids = {};
      homes.forEach(function (h) { ids[h.legacyId] = true; });
      var affected = inv.projects.filter(function (p) { return ids[p.homeLegacyId] && p.state !== 'moved' && p.ticked; }).length;
      var moved = run.moved.filter(function (r) { return r.kind === 'project'; }).length;
      var inner = '<h2 class="mp-migrate__title">A Home couldn’t be moved</h2>';
      if (banner) inner += '<p class="mp-migrate__banner">' + esc(banner) + '</p>';
      inner += '<p>' + esc(names + ' couldn’t be moved to your account (' + reason + '). ') + '<strong>' + esc('Its ' + plural(affected, 'Project hasn’t', 'Projects haven’t') + ' been moved') + '</strong>' + esc(' and ' + (affected === 1 ? 'is' : 'are') + ' still in this browser.') + (moved ? ' ' + esc(plural(moved, 'other Project was', 'other Projects were') + ' already moved.') : '') + '</p>';
      inner += checkReport(run.lastCheck);
      inner += actions([['blocked-try', 'Try again', true], ['blocked-without', 'Move these Projects without a Home'], ['blocked-keep', 'Keep them in this browser']]);
      return card(inner);
    }

    // Fix B: after an unknown outcome, say what the account check found —
    // what reached the account, what didn't, and what is still unconfirmed.
    function checkReport(c) {
      if (!c || !c.total) return '';
      var one = c.kind === 'homes' ? 'Home' : 'Project';
      var many = c.kind === 'homes' ? 'Homes' : 'Projects';
      if (c.landed + c.deleted + c.notLanded === 0) {
        return '<p class="mp-migrate__check" data-migrate-check>' + esc('We couldn’t check your account just now, so we don’t know yet whether that step reached it.') + '</p>';
      }
      var bits = ['We checked your account: ' + c.landed + ' of ' + plural(c.total, one, many) + ' in that step reached it.'];
      if (c.deleted) bits.push(plural(c.deleted, one + ' was', many + ' were') + ' deleted from your account earlier and won’t be added back.');
      if (c.notLanded) bits.push(plural(c.notLanded, one + ' hasn’t', many + ' haven’t') + ' been moved yet.');
      if (c.unverified) bits.push('We couldn’t confirm ' + plural(c.unverified, one.toLowerCase(), many.toLowerCase()) + ' yet.');
      return '<p class="mp-migrate__check" data-migrate-check>' + esc(bits.join(' ')) + '</p>';
    }

    function renderPaused() {
      // #16: while an earlier step's outcome is unknown, the only way on is
      // to check the account again ("Check again") -- never a resend.
      var checking = !!run.pendingCheck;
      var reason = checking || run.pauseReason === 'unknown'
        ? 'The connection was lost before your account confirmed the last step. Your Projects are still in this browser.'
        : failureText(run.pauseReason, false);
      var inner = '<h2 class="mp-migrate__title">Paused</h2><p>' + esc(reason) + '</p>';
      inner += checkReport(run.lastCheck);
      var next;
      if (checking) next = 'Nothing will be sent until we’ve checked your account. When you’re connected, tap Check again — nothing is resent until we know what reached it.';
      else if (run.pauseReason === 'offline') next = 'When you’re connected, tap Continue.';
      else next = 'Nothing will be sent until you continue.';
      inner += '<p>' + esc(next) + '</p>';
      inner += actions([['continue', checking ? 'Check again' : 'Continue', true], ['stop', 'Stop here']]);
      return card(inner);
    }

    function renderResults() {
      var movedP = run ? run.moved.filter(function (r) { return r.kind === 'project'; }).length : 0;
      var movedH = run ? run.moved.filter(function (r) { return r.kind === 'home'; }).length : 0;
      var inner = '';
      if (run && run.stopReason) {
        inner += '<h2 class="mp-migrate__title">Moving stopped</h2><p>' + esc(failureText(run.stopReason, false)) + '</p>';
      } else if (run && run.stoppedByCustomer) {
        // #17: never "0 … now saved" -- and never "nothing was saved" while a
        // step is unconfirmed.
        inner += '<h2 class="mp-migrate__title">Moving stopped</h2>';
        var stopped = stoppedText(run);
        if (stopped) inner += '<p>' + esc(stopped) + '</p>';
      } else {
        inner += '<h2 class="mp-migrate__title">' + esc(movedWhat(movedP, movedH) + ' now saved to your account') + '</h2>';
      }
      if (movedP || movedH) inner += '<p>' + esc(movedWhat(movedP, movedH) + ' moved. A copy is still in this browser.') + '</p>';
      if (run && run.changed.length) {
        inner += '<h3 class="mp-migrate__subtitle">Changed since you reviewed</h3><ul class="mp-migrate__list">' + run.changed.map(function (c) {
          return '<li>' + esc(q(c.row.kind === 'home' ? homeName(c.row) : projectName(c.row)) + ' ' + c.text) + '</li>';
        }).join('') + '</ul>';
      }
      var left = inv.projects.filter(function (p) { return !p.resolved && p.state !== 'moved' && p.state !== 'deleted_in_account' && p.state !== 'already_in_account'; }).length;
      if (left) inner += '<p class="mp-migrate__note">' + esc(plural(left, 'Project stays', 'Projects stay') + ' in this browser.') + '</p>';
      inner += actions([['done', 'Done', true]]);
      return card(inner);
    }

    // #17: "1 Home", "6 Projects and 1 Home" -- never "0 Projects and 1 Home".
    function movedWhat(p, h) {
      if (p && h) return plural(p, 'Project', 'Projects') + ' and ' + plural(h, 'Home', 'Homes');
      if (h) return plural(h, 'Home', 'Homes');
      return plural(p, 'Project', 'Projects');
    }

    // #17: what "Stop here" tells the customer about the last step.
    function stoppedText(r) {
      var u = r.uncertain || [];
      if (u.length) {
        var uh = u.filter(function (x) { return x.kind === 'home'; });
        var up = u.filter(function (x) { return x.kind === 'project'; });
        var what = u.length <= 3
          ? u.map(function (x) { return q(x.kind === 'home' ? homeName(x) : projectName(x)); }).join(', ')
          : [uh.length ? plural(uh.length, 'Home', 'Homes') : '', up.length ? plural(up.length, 'Project', 'Projects') : ''].filter(Boolean).join(' and ');
        return 'We couldn’t confirm whether the last step reached your account, so ' + what +
          ' may already be saved there. Nothing will be saved twice: next time you open My Projects, we’ll check your account first and only offer what isn’t there yet.';
      }
      if (r.pauseReason === 'offline') return 'You were offline, so nothing from the last step was sent.';
      return '';
    }

    function rowById(kind, id) {
      var list = kind === 'home' ? inv.homes : inv.projects;
      for (var i = 0; i < list.length; i++) if (list[i].legacyId === id) return list[i];
      return null;
    }

    function bind() {
      Array.prototype.forEach.call(container.querySelectorAll('[data-migrate-action]'), function (b) {
        b.addEventListener('click', function () { onAction(b.getAttribute('data-migrate-action')); });
      });
      Array.prototype.forEach.call(container.querySelectorAll('[data-migrate-tick]'), function (cb) {
        cb.addEventListener('change', function () {
          var row = rowById(cb.getAttribute('data-migrate-tick'), cb.getAttribute('data-id'));
          if (!row) return;
          toggleRow(row, cb.checked);
          render();
        });
      });
      Array.prototype.forEach.call(container.querySelectorAll('[data-migrate-edit]'), function (input) {
        input.addEventListener('input', function () {
          var row = rowById(input.getAttribute('data-migrate-edit'), input.getAttribute('data-id'));
          if (!row) return;
          onEditName(row, input.value);
          var count = container.querySelector('[data-migrate-count="' + cssEsc(row.legacyId) + '"]');
          if (count) count.textContent = input.value.length + ' / ' + MAX_NAME;
          var prompt = container.querySelector('[data-migrate-prompt="' + cssEsc(row.legacyId) + '"]');
          if (prompt) prompt.textContent = namePrompt(row, input.value);
          updateButtonsOnly();
        });
        input.addEventListener('change', function () { render(); });
      });
      Array.prototype.forEach.call(container.querySelectorAll('[data-migrate-row]'), function (b) {
        b.addEventListener('click', function () {
          var kind = b.getAttribute('data-kind') || 'project';
          var row = rowById(kind, b.getAttribute('data-id'));
          if (!row) return;
          var what = b.getAttribute('data-migrate-row');
          if (what === 'without-home') {
            row.withoutHome = true; row.ticked = true; row.checkedFp = null;
            // Only a Home problem is answered by this choice; a pending name
            // fix stays pending (its suggestion is accepted by Check changes).
            if (row.state === 'home_unavailable' || row.state === 'home_blocked') row.state = 'ready';
          }
          if (what === 'keep') { row.ticked = false; }
          if (what === 'use-move-date') { row.datePreference = 'import_time'; row.checkedFp = null; }
          planVersion += 1;
          render();
        });
      });
    }

    function cssEsc(s) { return String(s).replace(/["\\]/g, '\\$&'); }

    // Keeps typing responsive: only the buttons' enabled state changes.
    function updateButtonsOnly() {
      var ready = moveReadiness();
      var check = container.querySelector('[data-migrate-action="check-changes"]');
      var move = container.querySelector('[data-migrate-action="move"]');
      var anyEdited = showCheck();
      if (check) check.disabled = !checkEnabled();
      else if (anyEdited) render();
      if (move) move.disabled = !ready.ok;
    }

    function onAction(a) {
      if (a === 'review' || a === 'check-all') {
        if (a === 'check-all') inv.homes.concat(inv.projects).forEach(function (r) { r.checkedFp = null; });
        return startPreview();
      }
      if (a === 'not-now' || a === 'cancel') return defer();
      if (a === 'include-unscoped') return decideUnscoped(true);
      if (a === 'exclude-unscoped') return decideUnscoped(false);
      if (a === 'check-changes') return checkChanges();
      if (a === 'move') return startImport();
      if (a === 'blocked-try') return blockedTryAgain();
      if (a === 'blocked-without') return blockedMoveWithout();
      if (a === 'blocked-keep') return blockedKeep();
      // #16: one step at a time -- a second tap, or a stale button, can't
      // start another check or move while one is already running.
      if (a === 'continue') return state === 'PAUSED' ? continueAfterPause() : Promise.resolve();
      if (a === 'stop') return state === 'PAUSED' ? stopByCustomer() : undefined;
      if (a === 'done') return done();
    }

    return {
      scan: scan,
      getState: function () { return state; },
      // Test seam: what any edit does to the plan (older answers are ignored).
      _supersedeForTests: function () { planVersion += 1; },
      _inventory: function () { return inv; },
      _action: onAction
    };
  }

  window.MSGCloudMigration = {
    createController: createController,
    // Pure helpers, exposed for tests only.
    buildInventory: buildInventory,
    previewRequests: previewRequests,
    importHomeRequests: importHomeRequests,
    importProjectRequests: importProjectRequests,
    projectPayload: projectPayload,
    homePayload: homePayload,
    cleanBoard: cleanBoard,
    resolveRoom: resolveRoom,
    _setTimingForTests: function (t) {
      if (t && t.now) timing.now = t.now;
      if (t && t.sleep) timing.sleep = t.sleep;
      if (t && t.requestTimeoutMs) timing.requestTimeoutMs = t.requestTimeoutMs;
      sentAt = [];
    }
  };
})();
